<template>
  <div class="report-generator">
    <header class="dashboard-header">
      <div class="header-content">
        <div class="logo-section">
          <div class="kmpg-logo" @click="navigateToHome">
            <img src="/kpmg-logo-blue.svg" alt="KPMG" class="kmpg-logo-img" />
          </div>
          <h1>Report Generator</h1>
        </div>
        <div class="header-actions">
          <button @click="exportToPDF" :disabled="!selectedProject" class="export-btn">
            📄 Export to PDF
          </button>
          <button @click="goBack" class="back-btn">← Back to Dashboard</button>
        </div>
      </div>
    </header>

    <main class="main-content">
      <div class="container">
        
        <!-- Project Selection -->
        <div class="project-selection-card">
          <div class="card-header">
            <h2>Select Project for Report Generation</h2>
          </div>
          <div class="card-content">
            <div class="project-selector">
              <select v-model="selectedProjectId" @change="onProjectChange" class="project-dropdown">
                <option value="">Select a project to generate report...</option>
                <option v-for="project in availableProjects" :key="project.id" :value="project.id">
                  {{ project.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Report Content -->
        <div v-if="selectedProject" class="report-content" :class="{ 'fade-in': reportLoaded }">
          
          <!-- Statistics Chart -->
          <div class="statistics-card">
            <div class="card-header">
              <h2>Assessment Response Statistics</h2>
            </div>
            <div class="card-content">
              <div class="chart-container">
                <div class="chart-bars">
                  <div class="chart-bar" v-for="stat in responseStats" :key="stat.label">
                    <div class="bar-container">
                      <div class="bar-fill" :style="{ height: (stat.count / maxCount * 100) + '%' }"></div>
                    </div>
                    <div class="bar-label">{{ stat.label }}</div>
                    <div class="bar-count">{{ stat.count }}</div>
                  </div>
                </div>
                <div class="chart-title">Distribution of Responses Across All Assessment Questions</div>
              </div>
            </div>
          </div>

          <!-- Project Information -->
          <div class="project-info-card">
            <div class="card-header">
              <h2>Project Information</h2>
            </div>
            <div class="card-content">
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">Project Name:</span>
                  <span class="info-value">{{ selectedProject.name }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Implementing Agency:</span>
                  <span class="info-value">{{ selectedProject.implementingAgency }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Sector:</span>
                  <span class="info-value">{{ selectedProject.sector }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Stage:</span>
                  <span class="info-value">{{ selectedProject.stage }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Total Cost:</span>
                  <span class="info-value">${{ selectedProject.totalCost }}M</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Timeline:</span>
                  <span class="info-value">{{ selectedProject.timeline }} months</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Assessment Sections -->
          <div v-for="section in assessmentSections" :key="section.id" class="assessment-section-card">
            <div class="section-header">
              <h2>{{ section.title }}</h2>
              <div class="section-description">{{ section.description }}</div>
            </div>
            
            <div class="section-content">
              <div v-for="subsection in section.subsections" :key="subsection.id" class="subsection">
                <h3>{{ subsection.title }}</h3>
                
                <div v-for="question in subsection.questions" :key="question.id" class="question-item">
                  <div class="question-text">{{ question.text }}</div>
                  
                  <div class="answer-section">
                    <div class="answer-value" :class="getAnswerClass(question.answer)">
                      {{ formatAnswer(question.answer) }}
                    </div>
                    
                    <div v-if="question.comments" class="answer-comments">
                      <strong>Comments:</strong> {{ question.comments }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>

        <!-- No Project Selected -->
        <div v-else-if="!selectedProject && selectedProjectId === ''" class="no-project-message">
          <div class="message-content">
            <h3>📊 Generate Comprehensive Assessment Report</h3>
            <p>Select a project from the dropdown above to view its complete assessment report including all questions, answers, and response statistics.</p>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading project assessment data...</p>
        </div>
        
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'ReportGenerator',
  data() {
    return {
      selectedProjectId: '',
      reportLoaded: false,
      availableProjects: [
        {
          id: 1,
          name: 'Metro Line Extension Project',
          sector: 'Transport',
          stage: 'Implementation',
          totalCost: 450,
          implementingAgency: 'Department of Transportation',
          timeline: 48,
          fundingSource: 'PPP'
        },
        {
          id: 2,
          name: 'Water Treatment Facility',
          sector: 'Water',
          stage: 'Feasibility',
          totalCost: 280,
          implementingAgency: 'Ministry of Water Resources',
          timeline: 36,
          fundingSource: 'Government Budget'
        },
        {
          id: 3,
          name: 'Solar Power Plant',
          sector: 'Energy',
          stage: 'Concept',
          totalCost: 320,
          implementingAgency: 'Ministry of Energy',
          timeline: 42,
          fundingSource: 'International Loan'
        },
        {
          id: 4,
          name: 'Hospital Infrastructure',
          sector: 'Health',
          stage: 'Pre-feasibility',
          totalCost: 180,
          implementingAgency: 'Ministry of Health',
          timeline: 30,
          fundingSource: 'Grant'
        },
        {
          id: 5,
          name: 'Smart City Initiative',
          sector: 'Urban infrastructure',
          stage: 'Procurement',
          totalCost: 520,
          implementingAgency: 'Ministry of Urban Development',
          timeline: 60,
          fundingSource: 'PPP'
        }
      ]
    }
  },
  computed: {
    selectedProject() {
      return this.availableProjects.find(p => p.id == this.selectedProjectId)
    },
    assessmentSections() {
      if (!this.selectedProject) return []
      
      return [
        {
          id: 'basic-data',
          title: 'Basic Project Data',
          description: 'Fundamental project information and profile',
          subsections: [
            {
              id: 'basic-info',
              title: 'Basic Information',
              questions: [
                {
                  id: 'project-name',
                  text: 'Project Name',
                  answer: this.selectedProject.name,
                  comments: ''
                },
                {
                  id: 'implementing-agency',
                  text: 'Implementing Government Agency',
                  answer: this.selectedProject.implementingAgency,
                  comments: ''
                },
                {
                  id: 'stage',
                  text: 'Project Stage',
                  answer: this.selectedProject.stage,
                  comments: ''
                },
                {
                  id: 'privately-initiated',
                  text: 'Is this a privately initiated proposal?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'strategic-suitability',
          title: 'Strategic Suitability',
          description: 'Alignment with strategic objectives and priorities',
          subsections: [
            {
              id: 'section-a',
              title: 'Section A: Alignment with government priorities',
              questions: [
                {
                  id: 'strategic-importance',
                  text: 'Does the project have high strategic importance and alignment with government priorities?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'service-improvement',
                  text: 'Will the project lead to an improvement in service delivery?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'government-priorities',
                  text: 'Is the project aligned with current government priorities and policies?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            },
            {
              id: 'section-b',
              title: 'Section B: Economic and Social Impact',
              questions: [
                {
                  id: 'economic-impact',
                  text: 'Will the project have significant positive economic impact?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'social-benefit',
                  text: 'Does the project provide substantial social benefits to the community?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'preliminary-feasibility',
          title: 'Preliminary Feasibility',
          description: 'Technical and economic viability assessment',
          subsections: [
            {
              id: 'technical',
              title: 'Technical Feasibility',
              questions: [
                {
                  id: 'technical-viability',
                  text: 'Is the project technically feasible with current technology?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'technical-complexity',
                  text: 'Is the technical complexity manageable within project constraints?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            },
            {
              id: 'financial',
              title: 'Financial Viability',
              questions: [
                {
                  id: 'financial-returns',
                  text: 'Does the project demonstrate acceptable financial returns?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'funding-available',
                  text: 'Is adequate funding available or secured for the project?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'risk-assessment',
          title: 'Risk Assessment',
          description: 'Comprehensive risk identification and evaluation',
          subsections: [
            {
              id: 'construction-risks',
              title: 'Construction and Implementation Risks',
              questions: [
                {
                  id: 'construction-risk',
                  text: 'Are construction risks adequately identified and manageable?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'timeline-risk',
                  text: 'Is there significant risk of project delays?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            },
            {
              id: 'operational-risks',
              title: 'Operational Risks',
              questions: [
                {
                  id: 'operational-complexity',
                  text: 'Are operational risks well understood and mitigated?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'maintenance-risk',
                  text: 'Is long-term maintenance risk acceptable?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'ppp-suitability',
          title: 'PPP Suitability',
          description: 'Public-private partnership viability analysis',
          subsections: [
            {
              id: 'market-interest',
              title: 'Market Interest and Competition',
              questions: [
                {
                  id: 'private-interest',
                  text: 'Is there demonstrated private sector interest in the project?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'competitive-market',
                  text: 'Is there sufficient competition in the relevant market?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'fiscal-affordability',
          title: 'Fiscal Affordability',
          description: 'Financial sustainability and budget impact',
          subsections: [
            {
              id: 'budget-impact',
              title: 'Budget and Fiscal Impact',
              questions: [
                {
                  id: 'budget-availability',
                  text: 'Is the project within available budget constraints?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'fiscal-sustainability',
                  text: 'Is the project fiscally sustainable in the long term?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'institutional-capability',
          title: 'Institutional Capability',
          description: 'Organizational readiness and capacity assessment',
          subsections: [
            {
              id: 'capacity',
              title: 'Management and Technical Capacity',
              questions: [
                {
                  id: 'management-capacity',
                  text: 'Does the implementing agency have adequate management capacity?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'technical-expertise',
                  text: 'Is sufficient technical expertise available?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'climate-risk',
          title: 'Climate Risk Exposure',
          description: 'Environmental impact and climate vulnerability',
          subsections: [
            {
              id: 'climate-vulnerability',
              title: 'Climate Vulnerability Assessment',
              questions: [
                {
                  id: 'climate-resilience',
                  text: 'Is the project designed to be climate resilient?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'environmental-impact',
                  text: 'Has environmental impact been adequately assessed?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        },
        {
          id: 'climate-smart-ppp',
          title: 'Climate-Smart PPP',
          description: 'Sustainable development and climate resilience',
          subsections: [
            {
              id: 'sustainability',
              title: 'Sustainability and Green Innovation',
              questions: [
                {
                  id: 'green-technology',
                  text: 'Does the project incorporate green technologies or sustainable practices?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                },
                {
                  id: 'carbon-footprint',
                  text: 'Will the project help reduce overall carbon footprint?',
                  answer: this.generateRandomAnswer(),
                  comments: this.generateRandomComment()
                }
              ]
            }
          ]
        }
      ]
    },
    responseStats() {
      const stats = { yes: 0, no: 0, uncertain: 0, skip: 0, na: 0 }
      
      this.assessmentSections.forEach(section => {
        section.subsections.forEach(subsection => {
          subsection.questions.forEach(question => {
            const answer = question.answer?.toLowerCase()
            if (Object.prototype.hasOwnProperty.call(stats, answer)) {
              stats[answer]++
            }
          })
        })
      })
      
      return [
        { label: 'Yes', count: stats.yes },
        { label: 'No', count: stats.no },
        { label: 'Uncertain', count: stats.uncertain },
        { label: 'Skip', count: stats.skip },
        { label: 'N/A', count: stats.na }
      ]
    },
    maxCount() {
      return Math.max(...this.responseStats.map(stat => stat.count))
    }
  },
  methods: {
    generateRandomAnswer() {
      const answers = ['yes', 'no', 'uncertain', 'skip', 'na']
      const weights = [0.4, 0.2, 0.2, 0.1, 0.1] // Higher probability for 'yes'
      
      const projectSeed = this.selectedProjectId || 1
      const random = (projectSeed * 0.123456) % 1
      
      let cumulative = 0
      for (let i = 0; i < answers.length; i++) {
        cumulative += weights[i]
        if (random < cumulative) {
          return answers[i]
        }
      }
      return answers[0]
    },
    generateRandomComment() {
      const comments = [
        'This assessment is based on preliminary analysis and may require further detailed study.',
        'Additional stakeholder consultation may be needed to validate this assessment.',
        'The evaluation considers current market conditions and regulatory framework.',
        'This finding aligns with similar projects in the sector.',
        '', // Some questions have no comments
        '',
        'Further technical evaluation recommended during next project phase.',
        'Risk mitigation strategies should be developed for identified concerns.'
      ]
      
      const projectSeed = this.selectedProjectId || 1
      const random = (projectSeed * 0.789012) % 1
      return comments[Math.floor(random * comments.length)]
    },
    onProjectChange() {
      this.reportLoaded = false
      setTimeout(() => {
        this.reportLoaded = true
      }, 300)
    },
    formatAnswer(answer) {
      const answerMap = {
        'yes': 'Yes',
        'no': 'No',
        'uncertain': 'Uncertain',
        'skip': 'Skip',
        'na': 'N/A'
      }
      return answerMap[answer?.toLowerCase()] || answer
    },
    getAnswerClass(answer) {
      const answerLower = answer?.toLowerCase()
      switch (answerLower) {
        case 'yes': return 'answer-yes'
        case 'no': return 'answer-no'
        case 'uncertain': return 'answer-uncertain'
        case 'skip': return 'answer-skip'
        case 'na': return 'answer-na'
        default: return 'answer-default'
      }
    },
    async exportToPDF() {
      if (!this.selectedProject) return
      
      try {
        // Simple PDF export simulation - in a real app, you'd use a library like jsPDF or html2pdf
        const reportContent = this.generatePDFContent()
        
        // Create a blob with HTML content for download
        const blob = new Blob([reportContent], { type: 'text/html' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.selectedProject.name.replace(/\s+/g, '_')}_Assessment_Report.html`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        alert('Report exported successfully! (Note: In a production environment, this would generate a proper PDF file)')
      } catch (error) {
        console.error('Export failed:', error)
        alert('Export failed. Please try again.')
      }
    },
    generatePDFContent() {
      const project = this.selectedProject
      const stats = this.responseStats
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${project.name} - Assessment Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { background: #0056b3; color: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
            .section { margin-bottom: 30px; border: 1px solid #ddd; border-radius: 8px; padding: 20px; }
            .question { margin-bottom: 15px; }
            .answer { font-weight: bold; color: #0056b3; }
            .comments { font-style: italic; color: #666; margin-top: 5px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${project.name}</h1>
            <p>Assessment Report - Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="section">
            <h2>Response Statistics</h2>
            ${stats.map(stat => `<p>${stat.label}: ${stat.count}</p>`).join('')}
          </div>
          
          ${this.assessmentSections.map(section => `
            <div class="section">
              <h2>${section.title}</h2>
              <p>${section.description}</p>
              ${section.subsections.map(subsection => `
                <h3>${subsection.title}</h3>
                ${subsection.questions.map(question => `
                  <div class="question">
                    <p><strong>Q:</strong> ${question.text}</p>
                    <p class="answer"><strong>A:</strong> ${this.formatAnswer(question.answer)}</p>
                    ${question.comments ? `<p class="comments">Comments: ${question.comments}</p>` : ''}
                  </div>
                `).join('')}
              `).join('')}
            </div>
          `).join('')}
        </body>
        </html>
      `
    },
    goBack() {
      this.$emit('navigate', 'dashboard')
    },
    navigateToHome() {
      this.$emit('navigate', 'landing')
    }
  }
}
</script>

<style scoped>
.report-generator {
  min-height: 100vh;
  background: rgb(229, 229, 229);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.dashboard-header {
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.kmpg-logo {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.kmpg-logo:hover {
  transform: scale(1.05);
}

.kmpg-logo-img {
  height: 32px;
  width: auto;
}

.logo-section h1 {
  font-size: 1.8rem;
  color: #0056b3;
  margin: 0;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.export-btn {
  background: #0056b3;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.export-btn:hover:not(:disabled) {
  background: #004494;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 86, 179, 0.3);
}

.export-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.back-btn {
  background: transparent;
  color: #0056b3;
  border: 1px solid #0056b3;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #0056b3;
  color: white;
}

.main-content {
  padding: 2rem 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Cards */
.project-selection-card,
.statistics-card,
.project-info-card,
.assessment-section-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  margin-bottom: 2rem;
  transition: transform 0.3s, box-shadow 0.3s;
}

.project-selection-card:hover,
.statistics-card:hover,
.project-info-card:hover,
.assessment-section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.12);
}

.card-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f0f0f0;
}

.card-header h2 {
  font-size: 1.5rem;
  color: #0056b3;
  margin: 0;
  font-weight: 600;
}

.card-content {
  padding: 2rem;
}

/* Project Selector */
.project-selector {
  max-width: 500px;
}

.project-dropdown {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

.project-dropdown:focus {
  outline: none;
  border-color: #0056b3;
}

/* Statistics Chart */
.chart-container {
  text-align: center;
}

.chart-bars {
  display: flex;
  justify-content: center;
  align-items: end;
  gap: 2rem;
  height: 200px;
  margin-bottom: 1rem;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.bar-container {
  height: 150px;
  width: 40px;
  background: #f0f0f0;
  border-radius: 4px;
  display: flex;
  align-items: end;
  margin-bottom: 0.5rem;
}

.bar-fill {
  width: 100%;
  background: #0056b3;
  border-radius: 4px;
  transition: height 0.6s ease;
  min-height: 2px;
}

.bar-label {
  font-size: 0.9rem;
  color: #333;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.bar-count {
  font-size: 1.2rem;
  color: #0056b3;
  font-weight: 700;
}

.chart-title {
  font-size: 1.1rem;
  color: #666;
  margin-top: 1rem;
}

/* Project Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #0056b3;
}

.info-label {
  font-weight: 600;
  color: #333;
}

.info-value {
  color: #0056b3;
  font-weight: 600;
}

/* Assessment Sections */
.section-header {
  background: #0056b3;
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 12px 12px 0 0;
}

.section-header h2 {
  font-size: 1.4rem;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.section-description {
  font-size: 0.9rem;
  opacity: 0.9;
}

.section-content {
  padding: 2rem;
}

.subsection {
  margin-bottom: 2rem;
}

.subsection h3 {
  font-size: 1.2rem;
  color: #0056b3;
  margin: 0 0 1rem 0;
  font-weight: 600;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.question-item {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #e0e0e0;
}

.question-text {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.answer-section {
  margin-left: 1rem;
}

.answer-value {
  display: inline-block;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.answer-yes {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.answer-no {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.answer-uncertain {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.answer-skip {
  background: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

.answer-na {
  background: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

.answer-default {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.answer-comments {
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
  line-height: 1.4;
  margin-top: 0.5rem;
}

/* Report Content */
.report-content {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease;
}

.report-content.fade-in {
  opacity: 1;
  transform: translateY(0);
}

/* No Project Message */
.no-project-message {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  padding: 3rem;
  text-align: center;
}

.message-content h3 {
  color: #0056b3;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.message-content p {
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Loading State */
.loading-state {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
  padding: 3rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #0056b3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  color: #666;
  font-size: 1.1rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .info-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .header-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .export-btn,
  .back-btn {
    width: 100%;
    justify-content: center;
  }
  
  .chart-bars {
    gap: 1rem;
  }
  
  .chart-bar {
    min-width: 60px;
  }
  
  .bar-container {
    width: 30px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .info-item {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 1rem;
  }
  
  .card-header,
  .card-content,
  .section-content {
    padding: 1rem;
  }
  
  .section-header {
    padding: 1rem;
  }
  
  .chart-bars {
    gap: 0.5rem;
    height: 150px;
  }
  
  .bar-container {
    height: 100px;
    width: 25px;
  }
}
</style>